import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const buttonVariants = cva(
	'inline-flex items-center justify-center rounded-md text-body-sm font-semibold outline-none ring-ring ring-offset-2 ring-offset-background transition-colors focus-within:ring-2 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'bg-accent text-white ring-accent-70 hover:bg-accent-130 disabled:bg-accent-40 disabled:text-accent-70',
				lined:
					'border border-secondary-80 text-secondary-80 ring-secondary-40 hover:bg-primary-70 disabled:border-secondary-40 disabled:text-secondary-40',
				primary:
					'bg-primary text-primary-170 ring-primary-70 hover:bg-primary-70 disabled:bg-primary-40 disabled:text-primary-90',
				secondary:
					'bg-secondary-50 text-secondary ring-secondary-40 hover:bg-secondary-40 disabled:bg-secondary-30 disabled:text-secondary-50',
				ghost:
					'text-accent-120 underline underline-offset-2 ring-accent-70 hover:bg-accent-60',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
			},
			size: {
				default: 'h-10 px-4 py-2',
				icon: 'h-10 w-10',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				wide: 'px-24 py-5',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button'
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
